import api from './api';
import type { LoginResponse } from '../types';
import type { User } from '../types';

// ── Storage key ───────────────────────────────────────────────────────────────
// sessionStorage persists across React Router navigations AND page reloads
// within the same browser tab, but is wiped on tab-close (unlike localStorage).
export const TOKEN_KEY = 'permus-it-token';

// ── Post-login redirect ───────────────────────────────────────────────────────
// Where an unauthenticated visit was actually headed (e.g. a ticket link from an
// email notification) before PrivateLayout bounced it to /login. sessionStorage
// survives the full Azure AD redirect round-trip (same tab, same origin on
// return), which is exactly what's needed here — the browser leaves for
// login.microsoftonline.com and comes back to /auth/callback, but this value is
// still there waiting. Read once, at successful login, then cleared.
const POST_LOGIN_REDIRECT_KEY = 'permus-it-post-login-redirect';

/** Called by PrivateLayout right before it redirects an unauthenticated visit to
 *  /login — remembers the path so the login flow can send the user back here
 *  instead of always landing on the Dashboard. */
export function rememberPostLoginRedirect(path: string): void {
  try { sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path); } catch { /* ignore */ }
}

/** Called once login actually succeeds (SSO callback, or password login) —
 *  returns the path to land on, defaulting to /dashboard if nothing (or nothing
 *  sensible) was remembered, and always clears the stored value either way so a
 *  stale target never leaks into a later, unrelated login. */
export function consumePostLoginRedirect(): string {
  let saved: string | null = null;
  try {
    saved = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch { /* ignore */ }
  if (!saved || saved === '/login' || saved === '/auth/callback' || !saved.startsWith('/')) return '/dashboard';
  return saved;
}

// ── Token expiry helper ───────────────────────────────────────────────────────
// Returns true if the token expires within the next 5 minutes (or is unreadable).
// Used to decide whether to call refreshToken() on startup.
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

export function isTokenExpiringSoon(token: string): boolean {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(b64));
    if (typeof exp !== 'number') return true;
    return exp * 1000 - Date.now() < EXPIRY_BUFFER_MS;
  } catch {
    return true; // unreadable → treat as stale
  }
}

// ── refreshToken — deduped ────────────────────────────────────────────────────
// Concurrent callers share one in-flight POST /auth/refresh promise so that
// multiple useAuth() instances (PrivateLayout, Sidebar) never fire simultaneous
// requests and exhaust the rate limiter.

let _refreshing = false;
let _refreshPromise: Promise<LoginResponse> | null = null;

export async function refreshToken(): Promise<LoginResponse> {
  if (_refreshing && _refreshPromise) return _refreshPromise;

  _refreshing = true;
  _refreshPromise = api
    .post<LoginResponse>('/auth/refresh')
    .then(res => {
      _refreshing = false;
      _refreshPromise = null;
      return res.data;
    })
    .catch(err => {
      _refreshing = false;
      _refreshPromise = null;
      throw err;
    });

  return _refreshPromise;
}

// ── getCurrentUser — deduped ──────────────────────────────────────────────────
// GET /auth/me uses the Bearer token in the Authorization header (not a cookie).
// Called instead of refreshToken() when a valid token already exists in memory/storage.
// Concurrent callers share one fetch so PrivateLayout + Sidebar don't double-hit.

let _userFetch: Promise<User> | null = null;

export async function getCurrentUser(): Promise<User> {
  if (_userFetch) return _userFetch;
  _userFetch = api
    .get<User>('/auth/me')
    .then(res => { _userFetch = null; return res.data; })
    .catch(err => { _userFetch = null; throw err; });
  return _userFetch;
}

// ── Username/password login (local accounts) ──────────────────────────────────
// `identifier` accepts either the username or the email address.
export async function passwordLogin(identifier: string, password: string): Promise<LoginResponse> {
  const res = await api.post<LoginResponse>('/auth/login', { identifier, password });
  return res.data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
}
